// PendingTicket.jsx
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { formatDate } from "../utils/general_util";
import TicketDetailsDialog from "../components/TicketDetailsDialog";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { roundTo } from "../utils/general_util";

function prettyTicketType(type) {
    if (!type) return "N/A";
    return type === "reactive" ? "Reactive" : "Proactive";
}

function prettyStatus(status) {
    if (!status) return "Pending";
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1);
}

const PendingTicket = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const ticketIdInUrl = searchParams.get("ticketId");

    const [keepUrlClean, setKeepUrlClean] = useState(Boolean(ticketIdInUrl));

    const [role, setRole] = useState("user");
    const isAdmin = role === "admin";

    const [ready, setReady] = useState(false);

    const [email, setEmail] = useState("");
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("bcr_desc");

    // Dialog state
    const [selectedTicket, setSelectedTicket] = useState(null);

    // Admin: created_by dropdown
    const [createdByFilter, setCreatedByFilter] = useState("all"); // "all" or email
    const [createdByOptions, setCreatedByOptions] = useState([]);  // dropdown options

    useEffect(() => {
        if (typeof window === "undefined") return;

        const storedRole = window.localStorage.getItem("role") || "user";
        setRole(storedRole);

        const storedEmail =
            window.localStorage.getItem("userEmail") ||
            window.localStorage.getItem("email") ||
            "";

        // Only require email for non-admin
        if (!storedEmail && storedRole !== "admin") {
            setError(
                "No user email found in localStorage. Pending tickets cannot be loaded."
            );
            setLoading(false);
            setReady(true);
            return;
        }

        setEmail(storedEmail);
        setReady(true);
    }, []);

    useEffect(() => {
        if (!ready) return;
        if (!isAdmin && !email) return;

        const fetchTickets = async () => {
            setLoading(true);
            setError("");

            try {
                const params = { status: "pending" };

                if (isAdmin) {
                    if (createdByFilter !== "all") {
                        params.created_by = createdByFilter;
                    }
                } else {
                    params.created_by = email;
                    params.reviewed_by = email;
                }

                const res = await axiosInstance.get("/ticket/", { params });
                const data = res.data || [];
                setTickets(data);

                // Populate dropdown options when admin is viewing "All users"
                if (isAdmin && createdByFilter === "all") {
                    const uniqueCreators = Array.from(
                        new Set(
                            data
                                .map((t) => t.created_by || t.createdBy)
                                .filter(Boolean)
                        )
                    ).sort((a, b) => String(a).localeCompare(String(b)));

                    setCreatedByOptions(uniqueCreators);
                }
            } catch (err) {
                console.error("Failed to load pending tickets:", err);
                setError("Failed to load pending tickets. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchTickets();
    }, [ready, isAdmin, email, createdByFilter]);

    // Open dialog if URL has ticketId (deep link), then clear query string
    useEffect(() => {
        if (loading) return;

        // IMPORTANT: don't auto-close when ticketId is absent
        if (!ticketIdInUrl) return;

        const match = tickets.find(
            (t) => String(t.ticketId) === String(ticketIdInUrl)
        );

        if (match) {
            setSelectedTicket(match);

            // Clear ALL search params after consuming the deep link
            setSearchParams({}, { replace: true });
        } else {
            toast.error("Ticket not found in pending tickets.");
            setError(
                "Ticket not found (it may no longer be pending or you may not have access)."
            );
            setSelectedTicket(null);
            setSearchParams({}, { replace: true });
        }
    }, [loading, tickets, ticketIdInUrl, setSearchParams]);

    function handleOpenDetails(ticket) {
        setSelectedTicket(ticket);

        // If the user came from a deep link, keep the URL with no search params
        if (keepUrlClean) {
            setSearchParams({}, { replace: true });
            return;
        }

        // Otherwise, keep your original behaviour (optional)
        const next = new URLSearchParams(searchParams);
        next.set("ticketId", ticket.ticketId);
        setSearchParams(next, { replace: false });
    }

    function handleCloseDetails() {
        setSelectedTicket(null);

        // Ensure no search params
        setSearchParams({}, { replace: true });

        // Optional: once they close, you can return to normal behaviour if you want
        // setKeepUrlClean(false);
    }

    const filteredAndSorted = useMemo(() => {
        let list = Array.isArray(tickets) ? [...tickets] : [];

        const term = search.trim().toLowerCase();
        if (term) {
            const isNumeric = /^\d+$/.test(term);
            list = list.filter((t) => {
                const title = (t.title || t.ticketTitle || "").toLowerCase();
                const idStr = String(t.ticketId || "");
                if (isNumeric) return idStr.includes(term) || title.includes(term);
                return title.includes(term);
            });
        }

        list.sort((a, b) => {
            if (sortBy.startsWith("bcr")) {
                const aBcr = Number(a.benefitCostRatio);
                const bBcr = Number(b.benefitCostRatio);

                const aValid = Number.isFinite(aBcr);
                const bValid = Number.isFinite(bBcr);

                // Push missing/invalid BCR to the bottom
                if (!aValid && !bValid) return 0;
                if (!aValid) return 1;
                if (!bValid) return -1;

                return sortBy === "bcr_asc" ? aBcr - bBcr : bBcr - aBcr;
            }

            const da = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const db = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return sortBy === "date_asc" ? da - db : db - da;
        });

        return list;
    }, [tickets, search, sortBy]);

    async function handleMarkAsFinished(ticketId, status) {
        setError("");
        try {
            if (status === "completed") {
                await axiosInstance.post(`/ticket/${ticketId}/complete`);
            } else if (status === "abandoned") {
                await axiosInstance.post(`/ticket/${ticketId}/abandon`);
            }

            setTickets((prev) => prev.filter((t) => t.ticketId !== ticketId));
            toast.success(`Ticket marked as ${status} successfully.`);
            handleCloseDetails();
        } catch (err) {
            setError(`Failed to mark ticket as ${status}. Please try again.`);
        }
    }

    return (
        <div className="px-4 py-6 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-6xl rounded-3xl bg-white/95 p-8 shadow-2xl ring-1 ring-slate-200">
                <header className="mb-6 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900 text-left">
                            Pending Tickets
                        </h1>
                        <p className="mt-1 text-sm text-slate-600 text-left">
                            Submitted tickets awaiting reviewer approval or rejection.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs sm:text-xs">
                        {(email || isAdmin) && (
                            <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
                                Logged as: {email || "Admin"}
                            </span>
                        )}
                    </div>
                </header>

                {error && (
                    <div className="mb-4 rounded-md border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-800">
                        {error}
                    </div>
                )}

                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex-1">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">
                            Search
                        </label>
                        <input
                            type="text"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            placeholder="e.g. 102 or 'Production efficiency'"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <div className="w-full sm:w-56">
                        <label className="mb-1 block text-xs font-semibold text-slate-700">
                            Sort By
                        </label>
                        <select
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="bcr_desc">BCR: Highest first</option>
                            <option value="bcr_asc">BCR: Lowest first</option>
                            <option value="date_desc">Date: Newest first</option>
                            <option value="date_asc">Date: Oldest first</option>
                        </select>
                    </div>

                    {isAdmin && (
                        <div className="w-full sm:w-72">
                            <label className="mb-1 block text-xs font-semibold text-slate-700">
                                Created by
                            </label>
                            <select
                                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-300"
                                value={createdByFilter}
                                onChange={(e) => setCreatedByFilter(e.target.value)}
                            >
                                <option value="all">All users</option>

                                {createdByOptions.length === 0 ? (
                                    <option value="" disabled>
                                        No creators found
                                    </option>
                                ) : (
                                    createdByOptions.map((creatorEmail) => (
                                        <option key={creatorEmail} value={creatorEmail}>
                                            {creatorEmail}
                                        </option>
                                    ))
                                )}
                            </select>
                        </div>
                    )}
                </div>

                {loading && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        Loading pending tickets...
                    </div>
                )}

                {!loading && !error && filteredAndSorted.length === 0 && (
                    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-600">
                        No pending tickets found{isAdmin ? "." : " for your account."}
                    </div>
                )}

                {!loading && !error && filteredAndSorted.length > 0 && (
                    <div className="mt-4 space-y-3">
                        {filteredAndSorted.map((t) => (
                            <article
                                key={t.ticketId}
                                role="button"
                                tabIndex={0}
                                onClick={() => handleOpenDetails(t)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleOpenDetails(t);
                                    }
                                }}
                                className="cursor-pointer rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-400"
                            >
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="flex-1 min-w-0 space-y-1.5 text-left">
                                        <p className="text-xs sm:text-sm text-slate-500">
                                            Ticket ID:{" "}
                                            <span className="font-semibold text-slate-800">
                                                {t.ticketId ?? "N/A"}
                                            </span>
                                        </p>

                                        <h2 className="text-sm sm:text-base font-semibold text-slate-900 line-clamp-2">
                                            {t.title || t.ticketTitle || "(No title)"}
                                        </h2>

                                        {t.description && (
                                            <p className="mt-1.5 text-xs sm:text-sm text-slate-600 line-clamp-3 break-words">
                                                {t.description}
                                            </p>
                                        )}

                                        <div className="text-xs text-slate-600">
                                            Created: {formatDate(t.createdAt)}
                                        </div>
                                    </div>

                                    <div className="mt-3 flex flex-row items-center justify-between gap-3 text-xs sm:mt-0 sm:flex-col sm:items-end sm:justify-start sm:text-xs">
                                        <div className="flex flex-row flex-wrap gap-2 sm:flex-col sm:items-end">
                                            <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-blue-100 shadow-sm">
                                                {prettyStatus(t.status)}
                                            </span>

                                            {t.ticketType && (
                                                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 ring-1 ring-slate-200 shadow-sm">
                                                    {prettyTicketType(t.ticketType)}
                                                </span>
                                            )}

                                            {t.benefitCostRatio && (
                                                <span className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-1 text-xs font-medium text-purple-700 ring-1 ring-purple-200 shadow-sm">
                                                    BCR: {t.benefitCostRatio ? roundTo(Number(t.benefitCostRatio), 1) : "N/A"}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}

                {selectedTicket && (
                    <TicketDetailsDialog
                        ticket={selectedTicket}
                        onClose={handleCloseDetails}
                        onMarkAsFinished={handleMarkAsFinished}
                        isFinished={false}
                        inlineNotesEditable={true}
                        currentUserEmail={email}
                    />
                )}
            </div>
        </div>
    );
};

export default PendingTicket;
